/**
 * API del servicio de credenciales TNO (tnoradio-event-ticket-service).
 *
 * Variables:
 * - REACT_APP_EVENT_TICKET_SERVICE_URL — opcional; base sin barra final (p. ej. http://localhost:5055 en dev).
 *   Si no se define, en el navegador se usa el mismo origen + `/ticket-api` (proxy Nginx en ads/tnocon),
 *   así el check-in y la credencial no dependen de CORS ni de un certificado en el puerto 5055.
 *
 * Check-in desde el navegador usa REACT_APP_EVENT_TICKET_ADMIN_KEY (solo builds de staff;
 * en producción ideal: proxy server-side que no exponga la clave).
 */

function ticketApiBase() {
  const fromEnv = (process.env.REACT_APP_EVENT_TICKET_SERVICE_URL || '')
    .trim()
    .replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location?.origin) {
    const page = window.location;
    if (fromEnv) {
      try {
        const u = new URL(fromEnv);
        const crossOrigin = u.origin !== page.origin;
        const directNode =
          u.port === '5055' || /^(\d{1,3}\.){3}\d{1,3}$/.test(u.hostname);
        const bothTnoNetwork =
          page.hostname.endsWith('tnonetwork.com') &&
          u.hostname.endsWith('tnonetwork.com');
        const onTnoProdHost = page.hostname.endsWith('tnonetwork.com');
        if (
          crossOrigin &&
          onTnoProdHost &&
          (directNode || bothTnoNetwork)
        ) {
          return `${page.origin}/ticket-api`;
        }
      } catch {
        /* seguir con fromEnv */
      }
      return fromEnv;
    }
    return `${page.origin}/ticket-api`;
  }
  return fromEnv;
}

export function isEventTicketApiConfigured() {
  return Boolean(ticketApiBase());
}

export async function fetchPublicTicketByToken(accessToken) {
  const b = ticketApiBase();
  if (!b) {
    return { ok: false, error: 'not_configured' };
  }
  const url = `${b}/api/v1/public/tickets/${encodeURIComponent(accessToken)}`;
  const res = await fetch(url, { method: 'GET', credentials: 'omit' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return {
      ok: false,
      error: data.error || 'request_failed',
      status: res.status,
    };
  }
  return { ok: true, ticket: data };
}

/**
 * @param {string} accessToken — UUID del QR
 * @param {string} adminApiKey — X-Admin-Api-Key
 * @param {string} [staffId] — opcional X-Staff-Id
 */
export async function postCheckin(accessToken, adminApiKey, staffId) {
  const b = ticketApiBase();
  if (!b) {
    return { ok: false, error: 'not_configured' };
  }
  const key = String(adminApiKey || '')
    .replace(/[\r\n\u2028\u2029]/g, '')
    .trim();
  if (!key) {
    return { ok: false, error: 'missing_admin_key' };
  }
  const staff = String(staffId || '')
    .replace(/[\r\n\u2028\u2029]/g, '')
    .trim();
  const headers = {
    'Content-Type': 'application/json',
    'X-Admin-Api-Key': key,
  };
  if (staff) {
    headers['X-Staff-Id'] = staff;
  }
  const res = await fetch(`${b}/api/v1/checkin`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ token: accessToken }),
    credentials: 'omit',
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}
